import axios, { AxiosInstance } from 'axios';
import { 
  AuthResponse, 
  LoginRequest, 
  Donor, 
  CreateDonorRequest, 
  Donation, 
  CreateDonationRequest,
  Receipt,
  UploadReceiptRequest,
  UploadDocumentPhotoRequest,
  UploadDocumentPhotoResponse,
  PaginatedResponse,
  PaginationParams,
  User
} from '@/types';
import { donorCacheService } from './donorCacheService';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Priorità: .env.local > .env > default
    // In produzione, VITE_API_URL viene passato come build arg
    this.baseURL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
    
    // In produzione, VITE_API_URL deve essere definito
    if (import.meta.env.PROD && !this.baseURL) {
      throw new Error('VITE_API_URL environment variable is required in production');
    }
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor per aggiungere il token di autenticazione
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          config.withCredentials = true;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor per gestire la scadenza del token
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              localStorage.setItem('access_token', response.access_token);
              localStorage.setItem('refresh_token', response.refresh_token);
              
              // Riprova la richiesta originale
              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh fallito, reindirizza al login
            this.logout();
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Autenticazione
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials, {
      withCredentials: true
    });
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/refresh', {
      refresh_token: refreshToken
    }, {
      withCredentials: true
    });
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<User>('/auth/profile');
    return response.data;
  }

  logout(): void {
    // Rimuovi token dal localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Rimuovi eventuali altri dati di sessione
    localStorage.removeItem('user_profile');
    localStorage.removeItem('last_sync');
    localStorage.removeItem('offline_mode');
    
    // Pulisci sessionStorage se usato
    sessionStorage.clear();
    
    // Reset dell'istanza axios per rimuovere eventuali header cached
    this.api.defaults.headers.common['Authorization'] = '';
  }

  // Donatori
  async createDonor(donor: CreateDonorRequest): Promise<Donor> {
    const response = await this.api.post<Donor>('/donors', donor);
    return response.data;
  }

  async getDonor(id: string): Promise<Donor> {
    const response = await this.api.get<Donor>(`/donors/${id}`);
    return response.data;
  }

  async updateDonor(id: string, donor: Partial<CreateDonorRequest>): Promise<Donor> {
    const response = await this.api.put<Donor>(`/donors/${id}`, donor);
    return response.data;
  }


  // Donazioni
  async createDonation(donation: CreateDonationRequest): Promise<Donation> {
    const response = await this.api.post<Donation>('/donations', donation);
    return response.data;
  }

  async getDonation(id: string): Promise<Donation> {
    const response = await this.api.get<Donation>(`/donations/${id}`);
    return response.data;
  }

  async getCollectorDonations(collectorId: string, params?: PaginationParams): Promise<PaginatedResponse<Donation>> {
    const response = await this.api.get<PaginatedResponse<Donation>>(`/donations/collector/${collectorId}`, { params });
    return response.data;
  }

  async updateDonation(id: string, updateData: Partial<CreateDonationRequest>): Promise<Donation> {
    const response = await this.api.put<Donation>(`/donations/${id}`, updateData);
    return response.data;
  }

  async getMyDonationStats(): Promise<any> {
    const response = await this.api.get('/donations/stats/my-donations');
    return response.data;
  }

  async getMyDonations(): Promise<Donation[]> {
    const response = await this.api.get('/donations/my-donations');
    return response.data;
  }

  // Ricevute
  async uploadReceipt(uploadData: UploadReceiptRequest, onProgress?: (progress: number) => void): Promise<Receipt> {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('donationId', uploadData.donationId);

    const response = await this.api.post<Receipt>('/receipts/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  async getReceipt(id: string): Promise<Receipt> {
    const response = await this.api.get<Receipt>(`/receipts/${id}`);
    return response.data;
  }

  async getReceiptDownloadUrl(id: string): Promise<{ downloadUrl: string; expiresIn: number }> {
    const response = await this.api.get<{ downloadUrl: string; expiresIn: number }>(`/receipts/${id}/download`);
    return response.data;
  }

  // Foto documento donazione
  async uploadDocumentPhoto(uploadData: UploadDocumentPhotoRequest, onProgress?: (progress: number) => void): Promise<UploadDocumentPhotoResponse> {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    if (uploadData.donationId) {
      formData.append('donationId', uploadData.donationId);
    }

    const response = await this.api.post<UploadDocumentPhotoResponse>('/s3/upload/document-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; services: any }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Utility per verificare la connessione
  async isOnline(): Promise<boolean> {
    try {
      await this.healthCheck();
      return true;
    } catch {
      return false;
    }
  }

  // === GESTIONE DONATORI CON STRATEGIA IBRIDA ===

  /**
   * Carica TUTTI i donatori dal server e li salva in cache
   * Chiamato al login per popolare la cache
   * Scarica tutti i donatori disponibili senza limiti
   */
  async loadAllDonorsToCache(): Promise<Donor[]> {
    try {
      console.log('📋 Loading all donors from server for cache...');
      
      // Carica donatori in batch (max 100 per volta)
      let allDonors: Donor[] = [];
      let page = 1;
      const limit = 100;
      let hasMore = true;
      
      while (hasMore) {
        const response = await this.api.get(`/donors?page=${page}&limit=${limit}`);
        const donors = response.data.data || [];
        
        if (donors.length === 0) {
          hasMore = false;
        } else {
          allDonors = [...allDonors, ...donors];
          page++;
          
          // Log progresso ogni 100 donatori
          if (allDonors.length % 100 === 0) {
            console.log(`📋 Loaded ${allDonors.length} donors so far...`);
          }
        }
      }
      
      // Salva nella cache
      await donorCacheService.saveDonorsToCache(allDonors);
      
      console.log(`✅ Loaded ${allDonors.length} donors to cache`);
      return allDonors;
    } catch (error) {
      console.error('❌ Error loading donors to cache:', error);
      throw error;
    }
  }

  /**
   * Ricerca donatori con strategia ibrida:
   * - Online: Chiama server + aggiorna cache
   * - Offline: Usa cache locale
   */
  async searchDonors(query: string, limit: number = 10): Promise<Donor[]> {
    const isOnline = await this.isOnline();
    
    if (isOnline) {
      // STRATEGIA ONLINE: Server first
      try {
        console.log('🌐 Online: Searching donors from server');
        const response = await this.api.get(`/donors?search=${encodeURIComponent(query)}&limit=${limit}`);
        const donors = response.data.data || [];
        
        // Aggiorna cache in background (non bloccante)
        this.updateCacheInBackground();
        
        console.log(`✅ Found ${donors.length} donors from server`);
        return donors;
      } catch (error) {
        console.error('❌ Server search failed, falling back to cache:', error);
        // Fallback alla cache se il server fallisce
        return this.searchDonorsFromCache(query, limit);
      }
    } else {
      // STRATEGIA OFFLINE: Cache fallback
      console.log('📱 Offline: Searching donors from cache');
      return this.searchDonorsFromCache(query, limit);
    }
  }

  /**
   * Ricerca donatori dalla cache locale
   */
  private async searchDonorsFromCache(query: string, limit: number = 10): Promise<Donor[]> {
    try {
      const cachedDonors = await donorCacheService.getCachedDonors();
      
      if (cachedDonors.length === 0) {
        console.log('⚠️ No cached donors available');
        return [];
      }

      // Filtra i donatori localmente
      const filteredDonors = cachedDonors.filter(donor => {
        const searchTerm = query.toLowerCase();
        return (
          donor.firstName?.toLowerCase().includes(searchTerm) ||
          donor.lastName?.toLowerCase().includes(searchTerm) ||
          donor.email?.toLowerCase().includes(searchTerm) ||
          donor.phone?.includes(searchTerm) ||
          donor.fiscalCode?.toLowerCase().includes(searchTerm)
        );
      });

      const result = filteredDonors.slice(0, limit);
      console.log(`📋 Found ${result.length} donors from cache (${cachedDonors.length} total cached)`);
      return result;
    } catch (error) {
      console.error('❌ Error searching from cache:', error);
      return [];
    }
  }

  /**
   * Aggiorna la cache in background (non bloccante)
   */
  private async updateCacheInBackground(): Promise<void> {
    try {
      // Non aspetta il risultato, esegue in background
      this.loadAllDonorsToCache().catch(error => {
        console.log('⚠️ Background cache update failed:', error);
      });
    } catch (error) {
      console.log('⚠️ Background cache update error:', error);
    }
  }

  /**
   * Forza l'aggiornamento della cache (chiamato dal tasto Sincronizza)
   */
  async refreshDonorsCache(): Promise<Donor[]> {
    console.log('🔄 Forcing donors cache refresh...');
    return await this.loadAllDonorsToCache();
  }

  /**
   * Ottiene informazioni sulla cache
   */
  getCacheInfo(): { hasData: boolean; lastUpdated: number | null; isValid: boolean } {
    return {
      hasData: donorCacheService.hasCachedDonors(),
      lastUpdated: donorCacheService.getLastUpdated(),
      isValid: donorCacheService.isCacheValid(),
    };
  }

  /**
   * Controlla duplicati donatore (email, CF, P.IVA)
   * Usa server se online, cache se offline
   */
  async checkDonorDuplicates(donorData: {
    email: string;
    fiscalCode?: string;
    vatNumber?: string;
  }): Promise<{
    hasDuplicates: boolean;
    duplicates: {
      email?: Donor;
      fiscalCode?: Donor;
      vatNumber?: Donor;
    };
  }> {
    console.log('🔍 checkDonorDuplicates called with:', donorData);
    const isOnline = await this.isOnline();
    console.log('🌐 Is online:', isOnline);
    
    if (isOnline) {
      // STRATEGIA ONLINE: Controlla sul server
      try {
        console.log('🌐 Online: Checking duplicates on server');
        const response = await this.api.post('/donors/check-duplicates', donorData);
        console.log('🌐 Server response:', response.data);
        return response.data;
      } catch (error) {
        console.error('❌ Server duplicate check failed, falling back to cache:', error);
        // Fallback alla cache se il server fallisce
        return this.checkDonorDuplicatesFromCache(donorData);
      }
    } else {
      // STRATEGIA OFFLINE: Controlla nella cache
      console.log('📱 Offline: Checking duplicates from cache');
      return this.checkDonorDuplicatesFromCache(donorData);
    }
  }

  /**
   * Controlla duplicati donatore dalla cache locale
   */
  private async checkDonorDuplicatesFromCache(donorData: {
    email: string;
    fiscalCode?: string;
    vatNumber?: string;
  }): Promise<{
    hasDuplicates: boolean;
    duplicates: {
      email?: Donor;
      fiscalCode?: Donor;
      vatNumber?: Donor;
    };
  }> {
    try {
      console.log('📱 Checking duplicates from cache...');
      const cachedDonors = await donorCacheService.getCachedDonors();
      console.log('📱 Cached donors count:', cachedDonors.length);
      
      const duplicates = {
        email: undefined as Donor | undefined,
        fiscalCode: undefined as Donor | undefined,
        vatNumber: undefined as Donor | undefined,
      };

      // Controlla email
      if (donorData.email) {
        console.log('📱 Checking email:', donorData.email);
        duplicates.email = cachedDonors.find(donor => 
          donor.email?.toLowerCase() === donorData.email.toLowerCase()
        );
        console.log('📱 Email duplicate found:', duplicates.email);
      }

      // Controlla codice fiscale
      if (donorData.fiscalCode) {
        console.log('📱 Checking fiscal code:', donorData.fiscalCode);
        duplicates.fiscalCode = cachedDonors.find(donor => 
          donor.fiscalCode?.toLowerCase() === donorData.fiscalCode?.toLowerCase()
        );
        console.log('📱 Fiscal code duplicate found:', duplicates.fiscalCode);
      }

      // Controlla partita IVA
      if (donorData.vatNumber) {
        console.log('📱 Checking VAT number:', donorData.vatNumber);
        duplicates.vatNumber = cachedDonors.find(donor => 
          donor.vatNumber?.toLowerCase() === donorData.vatNumber?.toLowerCase()
        );
        console.log('📱 VAT number duplicate found:', duplicates.vatNumber);
      }

      const hasDuplicates = !!(duplicates.email || duplicates.fiscalCode || duplicates.vatNumber);
      
      console.log(`📋 Duplicate check from cache: ${hasDuplicates ? 'FOUND' : 'NOT FOUND'}`, duplicates);
      return { hasDuplicates, duplicates };
    } catch (error) {
      console.error('❌ Error checking duplicates from cache:', error);
      return { hasDuplicates: false, duplicates: {} };
    }
  }
}

export const apiService = new ApiService();
export default apiService;
